import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, Button } from "reactstrap";
import LaddaButton, { EXPAND_RIGHT } from "react-ladda";
import { CircularProgress } from "../../UI/CircularProgress/CircularProgress";

import { updateMessage } from "../../../services/api/internalMessages";
import { setLegalContract } from "../../../services/api/person";
import { notificationsReload } from "../../../actions/entities";
import { FirstAccessModal } from "../FirstAccessModal/FirstAccessModal";
import { PersonalDataModal } from "../PersonalDataModal/PersonalDataModal";
import { getTransactionalPinURL } from "../../../services/api/authorizations";
import { getSession } from "../../../reducers/selectors";
import { getRedirectionData, setSessionRedirectionData } from "../../../utils/handleRedirectionData";
import { setRedirectionData as setRedirectionDataAPI } from "../../../services/api/entities";
import { showToast } from "../../../utils/helper";

export const Popup = React.memo(({ internalMessage, open, toggle, isBlocking }) => {
  const dispatch = useDispatch();
  const session = useSelector(getSession);
  const [redirectData, setRedirectData] = useState(null);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAbsolute, setLoadingAbsolute] = useState(false);
  const handleToggle = () => {
    if (isBlocking) {
    } else {
      toggle();
    }
  };

  useEffect(() => {
    if (session) {
      const newRedirectData = getRedirectionData();
      if (newRedirectData) {
        if (!redirectData) {
          setRedirectData(() => newRedirectData);
        } else {
          if (newRedirectData.id !== redirectData.id) {
            setRedirectData(() => newRedirectData);
          } else {
            setRedirectData(() => null);
          }
        }
      } else {
        setRedirectData(() => null);
      }
    }
  }, [session]);

  useEffect(() => {
    if (redirectData) {
      setLoadingAbsolute(() => true);
      if (redirectData.data.type === "FirstAccess") {
        handleAuthorizeFirstAccess(redirectData.data.payload);
      } else {
        handleAuthorize(redirectData.data.payload);
      }
      setSessionRedirectionData(false);
    }
  }, [redirectData]);

  const handlePreAuthorize = () => {
    getTransactionalPinURL()
      .then(({ response }) => {
        const pre_pin_validation_token = response.pre_pin_validation_token;
        const redirect_url = response.redirect_url;

        const newRedirectData = {
          id: pre_pin_validation_token,
          data: {
            type: "Popup",
            payload: { read: true, approved: true, InternalMessageId: internalMessage.InternalMessageId },
          },
        };

        setRedirectionDataAPI(newRedirectData)
          .then(({ response }) => {
            if (response.result.Code === 200) {
              window.location.href = redirect_url;
            } else {
              showToast("Error procesando autorización", true);
            }
          })
          .catch((err) => {
            console.error(err);
            showToast("Error procesando autorización", true);
          });
      })
      .catch((err) => {
        console.error(err);
        showToast("Error autorizando notificación", true);
      })
      .finally(() => {
        setLoading(() => false);
        dispatch(notificationsReload());
      });
  };

  const handleAuthorize = (payload) => {
    updateMessage(payload)
      .then(({ response }) => {
        if (response.result.Code === 200) {
          let message = "Notificación autorizada correctamente";
          switch (internalMessage.InternalMessageForm) {
            case "PersonalData":
              message = "Datos personales confirmados correctamente.";
          }

          showToast(message);
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        }
      })
      .catch((err) => {
        console.error(err);
        showToast("Error autorizando notificación", true);
      })
      .finally(() => {
        dispatch(notificationsReload());
      });
  };

  const handlePreAuthorizeFirstAccess = (contractId) => {
    setLoadingAbsolute(() => true);
    getTransactionalPinURL()
      .then(({ response }) => {
        const pre_pin_validation_token = response.pre_pin_validation_token;
        const redirect_url = response.redirect_url;

        const newRedirectData = {
          id: pre_pin_validation_token,
          data: {
            type: "FirstAccess",
            payload: {
              message: { read: false, approved: true, InternalMessageId: internalMessage.InternalMessageId },
              contract: { LegalContractId: contractId },
            },
          },
        };

        setRedirectionDataAPI(newRedirectData)
          .then(({ response }) => {
            if (response.result.Code === 200) {
              window.location.href = redirect_url;
            } else {
              setLoadingAbsolute(() => true);
              showToast("Error procesando autorización", true);
            }
          })
          .catch((err) => {
            setLoadingAbsolute(() => true);
            console.error(err);
            showToast("Error procesando autorización", true);
          });
      })
      .catch((err) => {
        setLoadingAbsolute(() => true);
        console.error(err);
        showToast("Error autorizando notificación", true);
      })
      .finally(() => {
        setLoading(() => false);
        dispatch(notificationsReload());
      });
  };

  const handleAuthorizeFirstAccess = (payload) => {
    if (!loadingUpdate) {
      setLoadingUpdate(() => true);
      updateMessage(payload.message)
        .then(({ response }) => {
          if (response.result.Code === 200) {
            setLegalContract(payload.contract)
              .then((res) => {
                if (res.response.result.Code !== 200) {
                  showToast("Error confirmando el contrato", true);
                } else {
                  showToast("Información enviada correctamente");

                  setTimeout(() => {
                    window.location.reload();
                  }, 3000);
                }
              })
              .catch((err) => {
                showToast("Error confirmando el contrato", true);
                console.error(err);
              })
              .finally(() => {
                dispatch(notificationsReload());
                setLoadingUpdate(() => true);
                setRedirectData(() => null);
              });
          }
        })
        .catch((err) => {
          console.error(err);
          showToast("Error enviando información", true);
        })
        .finally(() => {
          dispatch(notificationsReload());
        });
    }
  };

  if (loadingAbsolute)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );

  if (internalMessage.InternalMessageForm === "FirstAccess" && !internalMessage.InternalMessageRead)
    return (
      <FirstAccessModal
        approved={internalMessage.InternalMessageApproved}
        handleAuthorize={handlePreAuthorizeFirstAccess}
      />
    );

  if (internalMessage.InternalMessageForm === "PersonalData")
    return <PersonalDataModal handleAuthorize={handlePreAuthorize} />;

  return (
    <Modal isOpen={open} toggle={handleToggle}>
      <ModalHeader toggle={handleToggle}>{internalMessage.InternalMessageTitle}</ModalHeader>
      <ModalBody>{internalMessage.InternalMessageText}</ModalBody>
      <ModalFooter>
        {!isBlocking && <Button onClick={handleToggle}>Cancelar</Button>}
        <LaddaButton
          className="btn btn-ladda btn-primary"
          loading={loading}
          onClick={handlePreAuthorize}
          data-style={EXPAND_RIGHT}
        >
          Autorizar
        </LaddaButton>
      </ModalFooter>
    </Modal>
  );
});
